import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Sparkles, Hand, Edit3, CheckSquare } from 'lucide-react';

const fmt = (s) => {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
};

/**
 * Review queue for every generated LF/SF derivative (manual OR AI), with
 * batch approve/reject — separate from the per-clip render/download flow
 * in EditorReviewPanel. Only approved clips are eligible for Stage 3F
 * rendering; batch-approving here calls the SAME server-owned approval
 * field individual approval uses, never a parallel status.
 */
export function EditorClipReviewQueue({ clips, onBatchApprove, onSelectClip }) {
  const [selected, setSelected] = useState([]);
  const derived = (clips || []).filter((c) => !!c.master_composition_slice);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleAll = () => setSelected((s) => (s.length === derived.length ? [] : derived.map((c) => c.id)));

  if (derived.length === 0) {
    return (
      <p className="text-sm text-[#8A93A6] text-center py-6" data-testid="clip-review-queue-empty">
        No generated clips yet. Approve the Master, then generate LF/SF derivatives.
      </p>
    );
  }

  return (
    <div className="space-y-2" data-testid="editor-clip-review-queue">
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 text-xs text-[#8A93A6] cursor-pointer">
          <Checkbox checked={selected.length === derived.length && derived.length > 0} onCheckedChange={toggleAll} data-testid="queue-select-all" />
          Select all ({derived.length})
        </label>
        <div className="flex gap-1.5">
          <Button
            size="sm" variant="outline" className="h-7 text-xs border-white/10" disabled={selected.length === 0}
            onClick={() => { onBatchApprove(selected, true); setSelected([]); }} data-testid="queue-batch-approve"
          >
            <CheckSquare className="h-3.5 w-3.5 mr-1.5" />Approve Selected
          </Button>
          <Button
            size="sm" variant="ghost" className="h-7 text-xs text-[#8A93A6]" disabled={selected.length === 0}
            onClick={() => { onBatchApprove(selected, false); setSelected([]); }} data-testid="queue-batch-unapprove"
          >
            Un-approve
          </Button>
        </div>
      </div>

      <div className="space-y-1.5 max-h-96 overflow-y-auto">
        {derived.map((clip) => (
          <div key={clip.id} className="flex items-center gap-2 p-2 rounded-lg bg-white/5 border border-white/10" data-testid={`queue-row-${clip.id}`}>
            <Checkbox checked={selected.includes(clip.id)} onCheckedChange={() => toggle(clip.id)} data-testid={`queue-select-${clip.id}`} />
            <div className="min-w-0 flex-1">
              <p className="text-sm text-white truncate">{clip.name}</p>
              <p className="text-[11px] text-[#8A93A6] flex items-center gap-1.5 flex-wrap">
                <Badge className="bg-white/10 border-0 text-[9px] px-1.5">{clip.output_type.toUpperCase()}</Badge>
                {clip.generated_from === 'ai_suggestion' ? (
                  <span className="flex items-center gap-0.5 text-amber-300"><Sparkles className="h-3 w-3" />AI</span>
                ) : (
                  <span className="flex items-center gap-0.5"><Hand className="h-3 w-3" />Manual</span>
                )}
                {clip.source_master_version != null && <span>· from Master v{clip.source_master_version}</span>}
                <span>· {fmt(clip.out_point - clip.in_point)}</span>
                <span>· {clip.target_aspect_ratio || 'Master AR'}</span>
              </p>
            </div>
            <Badge
              className={`${clip.approved ? 'bg-green-500/10 text-green-400' : 'bg-white/10 text-[#8A93A6]'} border-0 text-[10px] shrink-0`}
              data-testid={`queue-approved-badge-${clip.id}`}
            >
              {clip.approved ? 'Approved' : 'Pending'}
            </Badge>
            <button
              onClick={() => onSelectClip(clip.id)} className="shrink-0 p-1.5 rounded-md text-[#8A93A6] hover:text-white hover:bg-white/10"
              title="Edit this clip" data-testid={`queue-edit-${clip.id}`}
            >
              <Edit3 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
